/* ============================================================
   AXION // SPECIFICATION — Layer 3: PARSE  (site-wide)
   The whole storefront behaves like a parser:
     A. COMPILE  — the hero headline (on load) and every major
        section heading (on scroll-in) resolve from monospace
        code-tokens into the final display type.
     B. DISASSEMBLE — collection names, nav links and footer
        labels break into their construction grid on hover.
   Reflow-free (real glyphs reserve width; scramble is an overlaid
   ::before). Reduced-motion + mobile safe. Self-contained.
   ============================================================ */
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var small = window.matchMedia && window.matchMedia('(max-width: 760px)').matches;
  var GLYPHS = '01<>/[]{}=+*·|XAZ';
  function g() { return GLYPHS.charAt((Math.random() * GLYPHS.length) | 0); }

  var css =
    '.ax-parse__c{position:relative;display:inline-block;color:transparent;}' +
    '.ax-parse__c::before{content:attr(data-s);position:absolute;left:0;top:0;width:100%;text-align:center;' +
      'font-family:var(--t-mono,ui-monospace,Menlo,monospace);color:var(--color-accent,#A67C4A);' +
      'opacity:.85;letter-spacing:-.04em;pointer-events:none;}' +
    '.ax-parse__c.is-set{color:inherit;}' +
    '.ax-parse__c.is-set::before{content:none;}' +
    '.ax-parse__caret{display:inline-block;width:.46ch;height:.82em;margin-left:.04em;vertical-align:-.04em;' +
      'background:var(--color-accent,#A67C4A);animation:ax-parse-blink 1s steps(1) infinite;}' +
    '@keyframes ax-parse-blink{50%{opacity:0;}}' +
    /* word wrappers keep each word atomic so split headings never break mid-word */
    '.ax-parse__w,.ax-pg-w{display:inline-block;white-space:nowrap;}' +
    /* B. disassemble — works on a parent-hover (cards) or own-hover (.ax-dis) */
    '.ax-pg{display:inline-block;will-change:transform;' +
      'transition:transform .34s cubic-bezier(.2,.7,.2,1),outline-color .34s ease;' +
      'transition-delay:calc(var(--i,0)*16ms);outline:1px solid transparent;outline-offset:2px;}' +
    '.ax-ccard:hover .ax-ccard__name,.ax-ccard:focus-within .ax-ccard__name,' +
      '.ax-dis:hover,.ax-dis:focus-visible{font-family:var(--t-mono,ui-monospace,monospace);letter-spacing:.06em;}' +
    '.ax-ccard:hover .ax-ccard__name .ax-pg,.ax-ccard:focus-within .ax-ccard__name .ax-pg,' +
      '.ax-dis:hover .ax-pg,.ax-dis:focus-visible .ax-pg{' +
      'transform:translateY(calc(var(--o,0)*1px));' +
      'outline-color:color-mix(in srgb,currentColor 32%,transparent);}' +
    '@media (prefers-reduced-motion:reduce){.ax-pg{transition:none!important;}' +
      '.ax-ccard:hover .ax-ccard__name .ax-pg,.ax-dis:hover .ax-pg{transform:none;}}';
  var st = document.createElement('style'); st.textContent = css; document.head.appendChild(st);

  /* ---------- A. COMPILE ---------- */
  function compile(el) {
    if (el._axParsed || el.childElementCount > 0) return;          /* once, plain text only */
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text || text.length > 46) return;                          /* skip long (e.g. SEO product titles) */
    el._axParsed = true;
    var frag = document.createDocumentFragment(), cells = [];
    text.split(' ').forEach(function (word, wi) {
      if (wi > 0) frag.appendChild(document.createTextNode(' '));
      if (!word) return;
      var w = document.createElement('span'); w.className = 'ax-parse__w';
      for (var j = 0; j < word.length; j++) {
        var sp = document.createElement('span');
        sp.className = 'ax-parse__c'; sp.textContent = word[j];
        sp.setAttribute('data-s', g());
        w.appendChild(sp); cells.push(sp);
      }
      frag.appendChild(w);
    });
    var caret = document.createElement('span'); caret.className = 'ax-parse__caret';
    el.textContent = ''; el.appendChild(frag); el.appendChild(caret);
    var STEP = 30, SET = 190, t0 = Date.now();
    cells.forEach(function (c, i) { c._at = i * STEP; });
    var timer = setInterval(function () {
      var now = Date.now() - t0, done = 0;
      cells.forEach(function (c) {
        if (c.classList.contains('is-set')) { done++; return; }
        if (now >= c._at + SET) { c.classList.add('is-set'); done++; }
        else if (now >= c._at) { c.setAttribute('data-s', g()); }
      });
      if (done === cells.length) { clearInterval(timer); if (caret.parentNode) caret.remove(); }
    }, 36);
    setTimeout(function () { clearInterval(timer); cells.forEach(function (c) { c.classList.add('is-set'); }); if (caret.parentNode) caret.remove(); },
      cells.length * STEP + SET + 600);
  }

  if (!reduce && !small) {
    var hero = document.querySelector('.ax-hero__h1');
    if (hero) compile(hero);
    /* every major section heading, as it enters view */
    if ('IntersectionObserver' in window) {
      var heads = [].slice.call(document.querySelectorAll('main h1, main h2'))
        .filter(function (h) { return h !== hero && h.childElementCount === 0 && !h.classList.contains('ax-news__h'); });
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { compile(e.target); io.unobserve(e.target); } });
      }, { threshold: 0.45, rootMargin: '0px 0px -8% 0px' });
      heads.forEach(function (h) { io.observe(h); });
    }
  }

  /* ---------- B. DISASSEMBLE ---------- */
  function splitGrid(el) {
    if (el._axGrid || el.childElementCount > 0) return;
    var text = (el.textContent || '');
    if (!text.trim()) return;
    el._axGrid = true;
    var frag = document.createDocumentFragment(), idx = 0;
    text.split(' ').forEach(function (word, wi) {
      if (wi > 0) frag.appendChild(document.createTextNode(' '));
      if (!word) return;
      var w = document.createElement('span'); w.className = 'ax-pg-w';
      for (var j = 0; j < word.length; j++) {
        var sp = document.createElement('span');
        sp.className = 'ax-pg'; sp.textContent = word[j];
        sp.style.setProperty('--i', idx);
        sp.style.setProperty('--o', (idx % 2 ? -1 : 1) * (2 + (idx % 3)));
        w.appendChild(sp); idx++;
      }
      frag.appendChild(w);
    });
    el.textContent = ''; el.appendChild(frag);
  }
  /* collection card names (trigger on card hover) */
  document.querySelectorAll('.ax-ccard__name').forEach(splitGrid);
  /* own-hover labels site-wide: primary nav, footer headings + links */
  document.querySelectorAll('.ax-header__nav a, .ax-drawer__nav a, .foot nav h3, .foot nav a')
    .forEach(function (el) { el.classList.add('ax-dis'); splitGrid(el); });
})();
